/**
 * Toast Component
 * Individual toast notification with auto-dismiss and manual close
 */

import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import type { ToastMessage } from './toast.types';
import { useToastStore } from './toastStore';

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: {
    bg: 'bg-emerald-600',
    icon: 'text-emerald-100',
    progress: 'bg-emerald-400',
  },
  error: {
    bg: 'bg-red-600',
    icon: 'text-red-100',
    progress: 'bg-red-400',
  },
  warning: {
    bg: 'bg-amber-600',
    icon: 'text-amber-100',
    progress: 'bg-amber-400',
  },
  info: {
    bg: 'bg-blue-600',
    icon: 'text-blue-100',
    progress: 'bg-blue-400',
  },
};

export function Toast({ id, type, title, message, duration = 4000 }: ToastMessage) {
  const remove = useToastStore((s) => s.remove);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  // Auto-dismiss with progress bar
  useEffect(() => {
    const startTime = Date.now();
    const endTime = startTime + duration;

    const progressInterval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const percentage = (remaining / duration) * 100;
      setProgress(percentage);

      if (percentage <= 0) {
        clearInterval(progressInterval);
        handleClose();
      }
    }, 50);

    return () => clearInterval(progressInterval);
  }, [id, duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => remove(id), 200); // Wait for exit animation
  };

  const Icon = icons[type];
  const style = styles[type];

  return (
    <div
      className={`
        min-w-[300px] max-w-[400px] rounded-xl shadow-2xl overflow-hidden
        transform transition-all duration-200 ease-out
        ${isExiting ? 'opacity-0 translate-x-4 scale-95' : 'opacity-100 translate-x-0 scale-100'}
        ${style.bg}
      `}
      style={{
        animation: isExiting ? 'none' : 'toast-slide-in 0.3s ease-out',
      }}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`shrink-0 ${style.icon}`}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <p className="font-semibold text-white text-sm mb-0.5">{title}</p>
          )}
          <p className="text-white/90 text-sm leading-relaxed">{message}</p>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="shrink-0 text-white/70 hover:text-white transition-colors p-0.5 -m-0.5 rounded"
          aria-label="Tutup notifikasi"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-black/20">
        <div
          className={`h-full transition-all duration-100 ease-linear ${style.progress}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export default Toast;
